import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AppService } from '@nk/access-block';
import { isNkHttpError } from '@nk/api-client-lib';
import { AuthInfo, GENERIC_AUTH_SERVICE_TOKEN, GenericAuthService } from '@nk/auth-lib';
import { AppIdentityCompositeEntity } from '@nk/data-entities-lib';
import { NkToastrService } from '@nk/ui-lib';
import { filter, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'nk-login-view',
  templateUrl: 'login-view.component.html',
  standalone: false,
})
export class LoginViewComponent implements OnInit {
  public identityList$: Observable<AppIdentityCompositeEntity[]>;
  public identity$: Observable<AppIdentityCompositeEntity>;
  public authValid$: Observable<boolean>;
  public authInfo$: Observable<AuthInfo>;
  public loading = true;
  public isLoadingIdentities = true;
  public error: string | null;
  private readonly _destroy: DestroyRef = inject(DestroyRef);

  constructor(
    private _appService: AppService,
    private _router: Router,
    private _toastr: NkToastrService,
    @Inject(GENERIC_AUTH_SERVICE_TOKEN) private _authService: GenericAuthService,
  ) {}

  public ngOnInit(): void {
    this.isLoadingIdentities = true;
    this.identity$ = this._appService.identityDataCache.fetch().pipe(
      catchError((err: unknown) => {
        return throwError(() => {
          return 'error in identityDataCache';
        });
      }),
    );
    this.authInfo$ = this._authService.authInfo$;

    this.authValid$ = this._authService.authInfo$.pipe(
      switchMap((authInfo) => {
        if (!authInfo.isValid()) {
          this.loading = false;
          return of(false);
        }
        const ssoSubs = ['waad', 'okta']; // Connection type for SSO, if we add SAML add 'samlp' here.
        const match = ssoSubs.find((prefix) => authInfo.idTokenPayload.sub.startsWith(prefix));
        if (authInfo.idTokenPayload && match) {
          return this._appService.putAppEnterpriseIdentity().pipe(
            switchMap((appEnterpriseIdentity) => {
              if (appEnterpriseIdentity && appEnterpriseIdentity.message) {
                this._toastr.success(appEnterpriseIdentity.message);
              }
              return of(true);
            }),
          );
        }
        return of(true);
      }),
      catchError((error: unknown) => {
        let errorMessage = 'Unknown error';
        this.loading = false;
        if (isNkHttpError(error)) {
          errorMessage = error.error.message;
          if (this._authService.loginMessageDict[error.error.message]) {
            this._authService.logout(error.error.message);
          } else {
            this._authService.logout('custom', error.error.message);
          }
        } else {
          this._authService.logout('custom');
        }
        return throwError(() => {
          return errorMessage;
        });
      }),
    );

    this.identityList$ = this.authValid$.pipe(
      filter((isValid) => isValid),
      switchMap(() => {
        return this._appService.appIdentityCompositeListCache.fetch();
      }),
      tap(() => {
        this.isLoadingIdentities = false;
      }),
      catchError((error: unknown) => {
        this.isLoadingIdentities = false;
        if (isNkHttpError(error)) {
          this.error = error.error.message;
        }
        return throwError(() => error);
      }),
    );

    this.identityList$.pipe(takeUntilDestroyed(this._destroy)).subscribe((identityList) => {
      this.loading = false;
      if (identityList && identityList.length) {
        // When identityList$ is present, login view will show page  for choosing customer. TODO: make a seperate page

        const redirectUrl = this._authService.getRedirectUrl();

        // Will be redirected back to "accept-connection" page.
        if (redirectUrl && redirectUrl.includes('accept-connection')) {
          // Accept connection is a specific Enin page.
          this.onChoosingCustomer(identityList[0].app_customer.slug);
        } else {
          if (this._authService.customerSlug) {
            this.onChoosingCustomer(this._authService.customerSlug);
          } else if (identityList && identityList.length === 1) {
            // You only have one slug here, so lets just use the one we know you have access too.
            this.onChoosingCustomer(identityList[0].app_customer.slug);
          }
        }
      }
    });
  }

  public onChoosingCustomer(customerSlug: string): void {
    const redirectUrl = this._authService.getRedirectUrl();
    this._authService.setCustomerSlug(customerSlug);
    if (redirectUrl && redirectUrl.startsWith('/login')) {
      // Not sure what to do here. Dash? Show a message?
      this._router.navigate(['/dashboard'], { queryParams: { choosing_customer: true } });
    } else if (redirectUrl) {
      this._router.navigateByUrl(redirectUrl);
    } else {
      this._router.navigate(['/dashboard'], { queryParams: { choosing_customer: true } });
    }
    this._authService.popRedirectUrl();
  }

  public onLogoutClick(): void {
    this._authService.logout('userLogout');
  }

  public logoutAndRedirectToLogin(): void {
    this._authService.logout('userLogout');
  }

  public onLoginButton(): void {
    this._router.navigate(['/login']);
  }

  public onDashboardClick(): void {
    this._router.navigate(['/dashboard']);
  }
}
